(function (global) {
  'use strict';
  if (global.TPGSecurity) return;
  const purifier = global.DOMPurify(global);
  const commercePath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:cart(?:\/|\.|$)|products(?:\/|$)|collections(?:\/|$)|search(?:\/|\.|$)|recommendations\/products(?:\.|$)|variants\/|discount\/)/i;
  const blockedRoots = 'html,head,body,main,header,footer,#MainContent,#shopify-section-header,#shopify-section-footer';
  function formURL(input) {
    const url = new URL(String(input), global.location.origin);
    if (url.origin !== global.location.origin || !/^https?:$/.test(url.protocol) || url.username || url.password) throw new TypeError('Storefront forms must stay on this store.');
    return url.href;
  }
  async function request(input, options) {
    const url = new URL(formURL(input));
    if (!commercePath.test(url.pathname)) throw new TypeError('This request is outside the storefront commerce routes.');
    const discount = url.pathname.match(/^(.*\/)discount\/([^/]+)\/?$/);
    if (discount) {
      url.pathname = discount[1] + 'cart/update.js';
      url.search = '';
      options = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ discount: decodeURIComponent(discount[2]) }) };
    }
    // Resolve the current transport only after validation, so later observers remain in the path.
    return global.fetch(url.href, Object.assign({}, options || {}, { credentials: 'same-origin', mode: 'same-origin', redirect: 'error' }));
  }
  function resourceAllowed(value) {
    if (!value) return true;
    if (/^data:image\/(?:png|jpeg|gif|webp);base64,/i.test(value)) return true;
    if (value.startsWith('blob:' + global.location.origin + '/')) return true;
    try {
      const url = new URL(value, global.location.origin);
      return /^https?:$/.test(url.protocol) && !url.username && !url.password && (url.origin === global.location.origin || url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.port);
    } catch (_) { return false; }
  }
  function mediaFrameAllowed(value) {
    try {
      const url = new URL(value, global.location.origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return false;
      return (['www.youtube.com', 'www.youtube-nocookie.com'].includes(url.hostname) && /^\/embed\/[\w-]+\/?$/.test(url.pathname)) || (url.hostname === 'player.vimeo.com' && /^\/video\/\d+\/?$/.test(url.pathname));
    } catch (_) { return false; }
  }
  function setResource(target, name, value) {
    const text = value == null ? '' : String(value).trim();
    const values = name === 'srcset' ? text.split(',').map(part => part.trim().split(/\s+/)[0]) : [text];
    const allowed = target.nodeName === 'IFRAME' && name === 'src' ? mediaFrameAllowed(text) : values.every(resourceAllowed);
    if (text && allowed) target.setAttribute(name, text); else target.removeAttribute(name);
    return text;
  }
  function safeCSS(value) {
    value = value.replace(/\/\*[\s\S]*?\*\//g, '');
    if (/\\|\/\*|@import|@font-face|expression\s*\(|behavior\s*:|javascript:|(?:image-set|cross-fade|image|src)\s*\(/i.test(value)) return false;
    let allowed = true;
    const remaining = value.replace(/url\(\s*(['"]?)([^)'"\s]+)\1\s*\)/gi, (_, quote, url) => { if (!resourceAllowed(url)) allowed = false; return ''; });
    if (/url\s*\(/i.test(remaining)) allowed = false;
    return allowed;
  }
  const componentID = /^(?:CartDrawer(?:-[\w-]+)?|cart-(?:notification|icon-bubble)(?:-[\w-]+)?|(?:MainProduct|ProductInfo|MediaGallery|GalleryViewer|GalleryThumbnails|Inventory|Sku|Quantity|SubscriptionWidget|ProductLightbox|ProductTabs|ViewFullDetails|TNC-Checkbox|UpsellsBlock|quantity-breaks|variant-selects|price|sticky-atc)-[\w-]+)$/;
  const componentClass = /^(?:cart-drawer|cart-item--product-[\w-]+|product__title-[\w-]+|tnc-checkbox-warning-[\w-]+|cart-(?:progress|checkpoints|timer)(?:-[\w-]+)?|section-[\w-]+-padding|color-scheme-[\w-]+)$/;
  const compoundSelector = /^(?:[a-zA-Z][a-zA-Z0-9-]*)?(?:(?:[.#][a-zA-Z_][\w-]*)|(?::(?:checked|disabled|enabled|focus|focus-visible|hover|active|first-child|last-child|only-child|empty|before|after))|(?:::(?:before|after)))*$/;
  function componentSelector(selector) {
    // A small accepted grammar contains selectors by construction: no functions,
    // namespaces, escapes, attributes or universal selectors are executable here.
    const tokens = selector.trim().match(/[>+~]|[^\s>+~]+/g) || [];
    const anchor = tokens[0] && tokens[0].match(/^([#.])([a-zA-Z_][\w-]*)/);
    if (!anchor || !(anchor[1] === '#' ? componentID : componentClass).test(anchor[2])) return false;
    let compounds = 0, combinator = false;
    for (const token of tokens) {
      if (/^[>+~]$/.test(token)) {
        if (!compounds || combinator || compounds === 1 && token !== '>') return false;
        combinator = true;
      } else {
        if (!compoundSelector.test(token)) return false;
        compounds++; combinator = false;
      }
    }
    return compounds > 0 && !combinator;
  }
  function componentRules(rules, keyframes) {
    return Array.from(rules).every(rule => {
      if (keyframes) return rule.type === 8 && /^(?:from|to|\d+(?:\.\d+)?%)(?:\s*,\s*(?:from|to|\d+(?:\.\d+)?%))*$/.test(rule.keyText) && safeCSS(rule.cssText);
      if (rule.type === 1) return !(rule.cssRules && rule.cssRules.length) && rule.selectorText.split(',').every(componentSelector) && safeCSS(rule.style.cssText);
      if (rule.type === 4 || rule.type === 12) return componentRules(rule.cssRules, false);
      if (rule.type === 7) return /^inv(?:Blink|Ripple)-[\w-]+$/.test(rule.name) && componentRules(rule.cssRules, true);
      return false;
    });
  }
  function sanitizeStylesheet(value) {
    if (!safeCSS(value)) return null;
    try {
      // Constructed sheets parse without attaching or applying rules to the page.
      // Unsupported parsers fail closed for fetched styles; static theme CSS remains.
      const sheet = new global.CSSStyleSheet();
      if (typeof sheet.replaceSync !== 'function') return null;
      sheet.replaceSync(value);
      if (!sheet.cssRules.length || !componentRules(sheet.cssRules, false)) return null;
      return Array.from(sheet.cssRules, rule => rule.cssText).join('\n');
    } catch (_) { return null; }
  }
  purifier.addHook('uponSanitizeElement', node => {
    if (node.nodeName === 'SCRIPT' && (node.hasAttribute('src') || !/^(?:application\/json|application\/ld\+json)$/.test(node.type))) node.remove();
    if (node.nodeName === 'IFRAME' && !mediaFrameAllowed(node.getAttribute('src'))) node.remove();
    if (node.nodeName === 'STYLE') {
      const css = sanitizeStylesheet(node.textContent);
      if (css === null) node.remove(); else node.textContent = css;
    }
  });
  purifier.addHook('uponSanitizeAttribute', (node, attribute) => {
    const name = attribute.attrName.toLowerCase(), value = attribute.attrValue.trim();
    if (name === 'name' && value === 'id' && node.nodeName === 'INPUT' && node.type === 'hidden') attribute.forceKeepAttr = true;
    if (name === 'form' && ['BUTTON','INPUT','SELECT','TEXTAREA','OUTPUT','FIELDSET'].includes(node.nodeName) && /^(?:CartDrawer-Form|cart|product-form-[\w-]+|product-form-installment-[\w-]+)$/.test(value)) attribute.forceKeepAttr = true;
    if (name.startsWith('on') || ['srcdoc','ping','formaction','is'].includes(name)) attribute.keepAttr = false;
    else if (['src','poster','action','href','xlink:href','background','data-src','data-poster'].includes(name)) {
      if (!(node.nodeName === 'IFRAME' && name === 'src' ? mediaFrameAllowed(value) : resourceAllowed(value))) attribute.keepAttr = false;
      if (name === 'action') { try { formURL(value); } catch (_) { attribute.keepAttr = false; } }
    } else if (name === 'srcset' || name === 'data-srcset') {
      const safe = value.split(',').map(part => part.trim()).filter(part => resourceAllowed(part.split(/\s+/)[0]));
      if (safe.length) attribute.attrValue = safe.join(', '); else attribute.keepAttr = false;
    } else if (name === 'style' || node.namespaceURI === 'http://www.w3.org/2000/svg' && ['fill','stroke','filter','clip-path','mask','marker','marker-start','marker-mid','marker-end','color-profile','cursor'].includes(name)) {
      if (!safeCSS(value, false)) attribute.keepAttr = false;
    }
  });
  function sanitizeHTML(html) {
    if (typeof html !== 'string') throw new TypeError('Expected component HTML.');
    return purifier.sanitize(html, {
      FORCE_BODY: true, ADD_TAGS: ['script','iframe','style'], ADD_ATTR: ['allow','allowfullscreen'],
      FORBID_TAGS: ['object','embed','base','link','meta','foreignObject','annotation-xml'], FORBID_ATTR: ['srcdoc','ping','formaction','is'],
      CUSTOM_ELEMENT_HANDLING: { tagNameCheck: /^[a-z][a-z0-9]*-[a-z0-9-]+$/, attributeNameCheck: /^[a-z][a-z0-9_-]*$/, allowCustomizedBuiltInElements: false }
    });
  }
  function parseHTML(html) { return new global.DOMParser().parseFromString(sanitizeHTML(html), 'text/html'); }
  const cartRoots = new WeakSet(), checkoutButtons = new WeakSet();
  function rebindCartControls(root) {
    if (!root || root.nodeType !== 1) return;
    const drawers = root.matches('cart-drawer') ? [root] : [...root.querySelectorAll('cart-drawer')];
    const parent = root.closest('cart-drawer');
    if (parent && !drawers.includes(parent)) drawers.push(parent);
    drawers.forEach(drawer => {
      if (!cartRoots.has(drawer)) {
        cartRoots.add(drawer);
        drawer.addEventListener('click', event => { if (event.target.closest('.drawer__close') && typeof drawer.close === 'function') drawer.close(); });
      }
    });
    const scope = parent || root;
    const buttons = scope.matches('#CartDrawer-Checkout') ? [scope] : [...scope.querySelectorAll('#CartDrawer-Checkout')];
    buttons.forEach(button => {
      if (checkoutButtons.has(button)) return;
      checkoutButtons.add(button);
      if (/^checkoutLoad\(event\);?$/.test(button.getAttribute('onclick') || '')) button.removeAttribute('onclick');
      button.addEventListener('click', event => {
        const drawer = button.closest('cart-drawer') || scope;
        const tnc = drawer.querySelector('[id^="TNC-Checkbox-"]');
        if (tnc && tnc.dataset.checked !== 'true') {
          event.preventDefault();
          drawer.querySelectorAll('.tnc-checkbox-warning').forEach(warning => warning.classList.remove('hidden'));
          return;
        }
        if (event.defaultPrevented) return;
        button.classList.add('loading');
        const spinner = button.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');
        global.setTimeout(() => {
          if (global.location.pathname.includes('/checkout')) return;
          button.classList.remove('loading'); button.disabled = false;
          if (spinner) spinner.classList.add('hidden');
        }, 8000);
      });
    });
  }
  function writeHTML(target, html, outer) {
    if (!target || target.nodeType !== 1 || target.matches(blockedRoots)) throw new TypeError('Global page containers cannot be replaced.');
    const safe = sanitizeHTML(html == null ? '' : String(html));
    if (outer) { const parent = target.parentElement; target.outerHTML = safe; if (parent) rebindCartControls(parent); }
    else { target.innerHTML = safe; rebindCartControls(target); }
    return safe;
  }
  function hydrateQuickAdd(root) {
    if (!root || root.nodeType !== 1 || !root.closest('quick-add-modal')) throw new TypeError('Quick Add hydration needs its component root.');
    if (global.Shopify && global.Shopify.PaymentButton) global.Shopify.PaymentButton.init();
    if (global.ProductModel) global.ProductModel.loadShopifyXR();
    if (global.TPGProductImageZoom) global.TPGProductImageZoom.init(root);
  }
  Object.defineProperty(global, 'TPGSecurity', { value: Object.freeze({ request, formURL, sanitizeHTML, parseHTML, writeHTML, resourceAllowed, setResource, rebindCartControls, hydrateQuickAdd }), writable: false, configurable: false });
  global.document.addEventListener('DOMContentLoaded', () => rebindCartControls(global.document.body), { once: true });
})(window);
